<template>
  <div>
    <TitleBar>
      <template v-slot:title>
        <span>{{ pageTitle }}</span>&nbsp;<span>(Beta)</span>
      </template>
      <template v-slot:subtitle>
        <div style="color: #37A0CD">
          <translate>Calculated on:</translate>&nbsp;
          <span v-if="report.modified != null">{{ report.modified | formatDate }}</span>
        </div>
      </template>
      <template v-slot:buttons>
        <GekkoButton :text="calculatedStatus" type="button" variant="outline-primary" class="mr-2"
                     @click="handleSubmit" />
        <GekkoButton 
          v-if="report.id"
          :text="$gettext('Download PDF')"
          type="button"
          class="mr-2"
          @click="downloadPDF(report.id)"
          variant="outline-primary"
        />
        <GekkoButton :text="$gettext('All VAT reports')" type="button" :to="{ name: 'VATReportBEList'}" />
      </template>
    </TitleBar>
    <div class="content">
      <LoadingSpinner v-if="loading" />
      <b-container class="report-info__msg__container warning">
        <span style="width: 90%;margin:auto;" v-translate>Please note that this report is in Beta, which means that although this report is open for public use, it is still in development and will receive future updates. If you have any comments about this report, please contact support@getgekko.com.</span>
      </b-container>
      <b-modal id="modal-multi-premium" size="lg" title="Get premium" no-stacking>
        <h4>You need to have a premium account to use this feature!</h4>
      </b-modal>
      <div v-if="!loading">
        <table>
          <tbody>
            <th class="col-header" colspan="4">Uitgaande handelingen</th>
            <tr>
              <td>00. Handelingen met 0% btw tarief, of handelingen onderworpen aan bijzondere regeling</td>
              <td class="numbers">{{ report.rooster_00 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>01. Handelingen met 6% btw tarief waarvoor de btw verschuldigd is door de aangever</td>
              <td class="numbers">{{ report.rooster_01 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>02. Handelingen met 12% btw tarief, waarvoor de btw verschuldigd is door de aangever</td>
              <td class="numbers">{{ report.rooster_02 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>03. Handelingen met 21% btw tarief, waarvoor de btw verschuldigd is door de aangever</td>
              <td class="numbers">{{ report.rooster_03 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>44. Diensten waarvoor de buitenlandse btw verschuldigd is door de medecontractant</td>
              <td class="numbers">{{ report.rooster_44 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>45. Handelingen waarvoor de btw verschuldigd is door de medecontractant</td>
              <td class="numbers">{{ report.rooster_45 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>46. Vrijgestelde intracommunautaire leveringen verricht in België en ABC-verkopen</td>
              <td class="numbers">{{ report.rooster_46 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>47. Andere vrijgestelde handelingen en andere handelingen verricht in het buitenland</td>
              <td class="numbers">{{ report.rooster_47 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>48. Bedrag van de uitgereikt creditnota's en de negatieve verbeteringen m.b.t. handelingen in rooster 44 en 46</td>
              <td class="numbers">{{ report.rooster_48 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>49. Bedrag van de uitgereikte creditnota's en de negatieve verbeteringen</td>
              <td class="numbers">{{ report.rooster_49 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <th class="col-header" colspan="4">Inkomende handelingen</th>
            </tr>
            <tr>
              <td>81. Bedrag van de inkomende handelingen en negatieve verbeteringen | Handelsgoederen, grond- en hulpstoffen</td>
              <td class="numbers">{{ 0 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>82. Bedrag van de inkomende handelingen en negatieve verbeteringen | Diensten en diversen goederen</td>
              <td class="numbers">{{ report.rooster_82 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>83. Bedrag van de inkomende handelingen rekening houdend met negatieve verbeteringen | Bedrijfsmiddelen</td>
              <td class="numbers">{{ report.rooster_83 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>84. Bedrag van de ontvangen creditnota's en de negatieve verbeteringen met betrekking tot de handelingen ingeschreven in de roosters 86 en 88</td>
              <td class="numbers">{{ report.rooster_84 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>85. Bedrag van de ontvangen creditnota's en de negatieve verbeteringen van handelingen uit overige handelingen van kader III</td>
              <td class="numbers">{{ report.rooster_85 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>86 Intracommunautaire verwervingen verricht in België en ABC-verkopen</td>
              <td class="numbers">{{ 0 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>87 Andere inkomende handelingen waarvoor de btw verschuldigd is door de aangever</td>
              <td class="numbers">{{ 0 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>88. Intracommunautaire diensten met verlegging van heffing</td>
              <td class="numbers">{{ report.rooster_88 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <th class="col-header" colspan="4">Verschuldigde belastingen</th>
            </tr>
            <tr>
              <td>54. Btw op de handelingen uit de roosters 01, 02 en 03</td>
              <td class="numbers">{{ report.rooster_54 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>55. Btw op de handelingen uit de roosters 86 en 88</td>
              <td class="numbers">{{ report.rooster_55 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>56. Btw op de handelingen uit de roosters 87, m.u.v. invoeren met verlegging van heffing</td>
              <td class="numbers">{{ 0 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>57. Btw op invoeren met verlegging van heffing</td>
              <td class="numbers">{{ 0 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>61. Diverse btw-regularisaties in het voordeel van de Staat</td>
              <td class="numbers">{{ 0 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>63. Terug te storten btw vermeld op ontvangen creditnota's</td>
              <td class="numbers">{{ report.rooster_63 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>XX. Totaal van de roosters 54, 55, 56, 57, 61 en 63</td>
              <td class="numbers">{{ report.rooster_xx | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <th class="col-header" colspan="4">Aftrekbare Belastingen</th>
            </tr>
            <tr>
              <td>59. Aftrekbare btw</td>
              <td class="numbers">{{ report.rooster_59 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>62. Diverse btw-regularisaties in het voordeel van de aangever</td>
              <td class="numbers">{{ 0 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>64. Te recuperen btw vermeld op uitgereikte creditnota's</td>
              <td class="numbers">{{ report.rooster_64 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>YY. Totaal van de roosters 59, 62 en 64</td>
              <td class="numbers">{{ report.rooster_yy | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <th class="col-header" colspan="4">Saldo</th>
            </tr>
            <tr>
              <td>71. Aan de staat verschuldigde belasting (XX-YY)</td>
              <td class="numbers">{{ report.rooster_71 | currency("EUR", 2) }}</td>
            </tr>
            <tr>
              <td>72. Sommen verschuldigd door de Staat (YY-XX)</td>
              <td class="numbers">{{ report.rooster_72 | currency("EUR", 2) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";

import { promptDownload } from "@/utils/"

import settingsPageSetup from "@/mixins/settingsPageSetup";
import GekkoButton from "@/components/GekkoButton";
import TitleBar from "@/components/TitleBar";
import VATBelgianReportService from "@/store/services/belgianvatreport.service";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner";

export default {
  name: "Taxreport",
  mixins: [settingsPageSetup],
  components: { TitleBar, GekkoButton, LoadingSpinner },
  data() {
    return {
      pageSettings: {
        title: this.$gettext("Create report"),
        showCompanyPicker: false
      },
      report: [],
      reportsToOverwrite: [],
      startDate: "",
      endDate: "",
      loading: true
    }
  },
  mounted() {
    if(this.isCreateView){
      if(Object.keys(this.reportInitialValues).length === 0) {
        this.$router.push({ name: 'VATReportBEList'})
        return
      } else {
        this.handleSubmit()
      }
    }else{
      VATBelgianReportService.get(this.$route.params.id, {extended: true}).then((response) => {
        this.report = response.data;
        let report_initial_values = {
          company: this.currentCompany.id,
          year: this.report.year,
          quarter: this.report.quarter,
        };
        this.$store.commit('taxreports/setBelgianReportInitialValues', report_initial_values);
        this.loading = false;
      })
    }
  },
  computed: {
    isCreateView: function(){
      return this.$route.path.search("create") > -1
    },
    calculatedStatus: function () {
      if (this.isCreateView) return this.$gettext("Calculate");
      return this.$gettext("Recalculate");
    },
    pageTitle: function (){
      var title = this.reportInitialValues.quarter;
      title += " " + this.reportInitialValues.year;
      return this.$gettext("Sales tax report for %period%").replace("%period%", title);
    },
    ...mapGetters({
      reportInitialValues: "taxreports/getBelgianReportInitialValues",
      currentCompany: "main/getCurrentCompany",
      currentUser: "main/getCurrentUser",
      visitingCompanyId: 'main/getVisitingCompanyId'
    })
  },
  filters: {
    formatDate: function (value) {
      var options = { year: "numeric", month: "long", day: "numeric" };
      return new Date(value).toLocaleDateString("en-US", options);
    }
  },
  methods: {
    getDaysInMonth(year, month) {
      var d = new Date(year, month - 1, 0).getDate();
      return d;
    },
    setupDates() {
      var q = this.reportInitialValues.quarter;
      const quarter = q.split("Q");
      var endMonth = quarter * 3;
      this.startDate = this.reportInitialValues.year + '-' + endMonth - 2 + '-' + 1;
      this.endDate = this.reportInitialValues.year + '-' + endMonth + '-' + this.getDaysInMonth(this.reportInitialValues.year, endMonth - 1);
    },
    handleSubmit() {
      this.setupDates();
      this.loading = true;
      var data = {};
      
      if (this.currentUser.is_premium) {
        if (this.isCreateView) {
          data['company'] = this.reportInitialValues.company;
          data['year'] = this.reportInitialValues.year;
          data['quarter'] = this.reportInitialValues.quarter;

          VATBelgianReportService.create(data).then((response) => {
            this.report = response.data;
            this.loading = false;
            this.$router.push({ name: 'VATReportBE', params: {id: response.data.id} })
          })
        } else {
          VATBelgianReportService.recalculate(this.report.id, data).then((response) => {
            this.report = response.data;
            this.loading = false;
          })
        }
      } else this.$store.commit('togglePremiumModal', 'web_trips_calculateroute')
    },
    downloadPDF (report_id) {
      VATBelgianReportService.getPDFBlob(report_id).then(response => {
        promptDownload(response)
      }).catch(error => console.error('Error downloading PDF:', error));
    },
  }
}
</script>
<style lang="scss" scoped>
table{
  width: 100%;
}
.report-info {
    align-items: center;
    height: 100%;
    &__msg {
        font-size: 14px;
        line-height: 1;
        .svg-inline--fa { margin-right: 10px; font-size: 16px; }
        &__container {
            display: flex;
            align-items: center;
        }
    }
    &__btn {
        text-align: right;
        font-size: 14px;
    }
}
.warning{
    border: none;
    border-radius: $radiusMd;
    background: $colorWarningBg;
    color: $colorWarningText;
    padding: 15px 0;
    .svg-inline--fa { margin-right: 5px;}
}
.numbers {
  width: 25%;
  text-align: right;
  font-weight: bold;
}
.unsupported{
  color: #ccc;
}
.correction {
  display: none;
  &.text-invoiced, &.text-danger { display: inline-block; }
}
</style>